/**
 * Licencia médica electrónica (LME): catálogos oficiales y reglas del formulario.
 *
 * Lo usan el formulario de licencias (muestra los campos de cada tipo y avisa lo que
 * falta) y el backend (rechaza lo que no cumple y arma lo que pide el Portal Integrado
 * para Entidades Empleadoras, PIEE). Los códigos son los del diccionario de datos de
 * la LME de la SUSESO. Si cada lado tuviera su copia, terminarían pidiendo cosas
 * distintas.
 *
 * CommonJS y sin spread, for...of ni desestructuración de arrays: el backend lo
 * carga con require y el frontend con import, y esa sintaxis haría que el build de
 * React le inyecte imports de helpers que no conviven con module.exports.
 */

// Tipos de licencia (campo 33 de la LME). El tipo 3 se separa en prenatal y
// postnatal porque solo el postnatal trae los datos del hijo. destino es dónde se
// tramita: las de la ley 16.744 van a la mutual o al ISL con la DIAT o DIEP, y SANNA
// se presenta en la COMPIN con su propio formulario.
const TIPOS_LICENCIA = [
    { valor: 'ENFERMEDAD_COMUN', codigo: 1, nombre: 'Enfermedad o accidente común', destino: 'PIEE' },
    { valor: 'PRORROGA_MEDICINA_PREVENTIVA', codigo: 2, nombre: 'Prórroga medicina preventiva', destino: 'PIEE' },
    { valor: 'MATERNAL_PRENATAL', codigo: 3, nombre: 'Maternal prenatal', destino: 'PIEE', maternal: true, admiteConcepcion: true },
    { valor: 'MATERNAL_POSTNATAL', codigo: 3, nombre: 'Maternal postnatal', destino: 'PIEE', maternal: true, admiteConcepcion: true, requiereHijo: true },
    { valor: 'ENFERMEDAD_GRAVE_HIJO', codigo: 4, nombre: 'Enfermedad grave del hijo menor de un año', destino: 'PIEE', requiereHijo: true },
    { valor: 'ACCIDENTE_TRABAJO', codigo: 5, nombre: 'Accidente del trabajo o del trayecto', destino: 'MUTUAL', leyAccidentes: true, requiereAccidente: true },
    { valor: 'ENFERMEDAD_PROFESIONAL', codigo: 6, nombre: 'Enfermedad profesional', destino: 'MUTUAL', leyAccidentes: true },
    { valor: 'PATOLOGIA_EMBARAZO', codigo: 7, nombre: 'Patología del embarazo', destino: 'PIEE', admiteConcepcion: true },
    { valor: 'SANNA', codigo: null, nombre: 'SANNA (ley 21.063)', destino: 'COMPIN', requiereHijo: true }
];

const TIPOS_REPOSO = [
    { valor: 'TOTAL', nombre: 'Total' },
    { valor: 'PARCIAL', nombre: 'Parcial' }
];

// Jornada del reposo parcial (campo 40).
const JORNADAS_REPOSO = [
    { valor: 'MANANA', nombre: 'Mañana' },
    { valor: 'TARDE', nombre: 'Tarde' },
    { valor: 'NOCHE', nombre: 'Noche' }
];

const OPERADORES = [
    { valor: 'IMED', nombre: 'I-Med' },
    { valor: 'MEDIPASS', nombre: 'Medipass' }
];

// Tipo de profesional (campo 48). La ley 21.746 solo admite estos tres.
const TIPOS_PROFESIONAL = [
    { valor: 'MEDICO', nombre: 'Médico cirujano' },
    { valor: 'DENTISTA', nombre: 'Cirujano dentista' },
    { valor: 'MATRONA', nombre: 'Matrona' }
];

// Ocupación del trabajador (campo 103). Se guarda en la ficha del colaborador.
const OCUPACIONES = [
    { valor: 11, nombre: 'Ejecutivo o directivo' },
    { valor: 12, nombre: 'Profesor' },
    { valor: 13, nombre: 'Otro profesional' },
    { valor: 14, nombre: 'Técnico' },
    { valor: 15, nombre: 'Vendedor' },
    { valor: 16, nombre: 'Administrativo' },
    { valor: 17, nombre: 'Operario, trabajador manual' },
    { valor: 18, nombre: 'Trabajador de casa particular' },
    { valor: 19, nombre: 'Otro' }
];

// Actividad laboral de la empresa (campo 102). Se guarda en los parámetros de la empresa.
const ACTIVIDADES_LABORALES = [
    { valor: 0, nombre: 'Agricultura, servicios agrícolas, silvicultura y pesca' },
    { valor: 1, nombre: 'Minas, petróleos y canteras' },
    { valor: 2, nombre: 'Industrias manufactureras' },
    { valor: 3, nombre: 'Construcción' },
    { valor: 4, nombre: 'Electricidad, gas y agua' },
    { valor: 5, nombre: 'Comercio' },
    { valor: 6, nombre: 'Transporte, almacenamiento y comunicaciones' },
    { valor: 7, nombre: 'Finanzas, seguros, bienes muebles y servicios técnicos, profesionales y otros' },
    { valor: 8, nombre: 'Servicios estatales, sociales, personales e internacionales' },
    { valor: 9, nombre: 'Actividad no especificada' }
];

// Motivos para no recepcionar una licencia en el PIEE (campo 9). Se omite el 4,
// "trabajador sector público con feriado legal": no aplica a empresas privadas.
const MOTIVOS_NO_RECEPCION = [
    { valor: 'INEXISTENCIA_RELACION_LABORAL', codigo: 1, nombre: 'Inexistencia de relación laboral' },
    { valor: 'RELACION_LABORAL_TERMINADA', codigo: 2, nombre: 'Relación laboral terminada', pideTermino: true },
    { valor: 'PERMISO_SIN_GOCE', codigo: 3, nombre: 'Trabajador con permiso sin goce de sueldo' },
    { valor: 'OTRAS_RAZONES', codigo: 5, nombre: 'Otras razones', pideGlosa: true }
];

const texto = (v) => (v === null || v === undefined ? '' : String(v).trim());

/** El elemento de un catálogo con ese valor, o null. */
function buscar(catalogo, valor) {
    const encontrado = catalogo.filter((item) => String(item.valor) === texto(valor));
    return encontrado.length ? encontrado[0] : null;
}

/** Reglas del tipo de licencia; objeto vacío si el tipo no existe. */
function reglasDelTipo(tipoLicencia) {
    return buscar(TIPOS_LICENCIA, tipoLicencia) || {};
}

/** 'YYYY-MM-DD' de una fecha en texto o Date, sin correrla por la zona horaria. */
function fechaTexto(fecha) {
    if (fecha instanceof Date) {
        const mm = String(fecha.getMonth() + 1).padStart(2, '0');
        const dd = String(fecha.getDate()).padStart(2, '0');
        return `${fecha.getFullYear()}-${mm}-${dd}`;
    }
    return texto(fecha).slice(0, 10);
}

/** La misma fecha un año después, en texto. */
function unAnioDespues(fecha) {
    const f = fechaTexto(fecha);
    return `${Number(f.slice(0, 4)) + 1}${f.slice(4)}`;
}

/**
 * Errores de una licencia; vacío si cumple. Recibe los campos del formulario:
 * id_colaborador, numero_licencia, tipo_licencia, fecha_emision, fecha_inicio,
 * dias_totales, fecha_recepcion, tipo_reposo, jornada_reposo, hijo_*,
 * accidente_fecha, accidente_trayecto, operador y profesional_tipo.
 */
function validarLicencia(licencia) {
    const errores = [];
    const tipo = buscar(TIPOS_LICENCIA, licencia.tipo_licencia);

    if (!texto(licencia.id_colaborador)) errores.push('Seleccione el colaborador.');
    if (!texto(licencia.numero_licencia)) errores.push('Ingrese el folio de la licencia.');
    if (!tipo) errores.push('Seleccione el tipo de licencia.');
    if (!texto(licencia.fecha_emision)) errores.push('Ingrese la fecha de emisión.');
    if (!texto(licencia.fecha_inicio)) errores.push('Ingrese la fecha de inicio del reposo.');
    if (!texto(licencia.fecha_recepcion)) errores.push('Ingrese la fecha en que la empresa recibió la licencia.');

    const dias = Number(licencia.dias_totales);
    if (!Number.isInteger(dias) || dias < 1) {
        errores.push('Los días de reposo deben ser un número entero mayor que cero.');
    }

    if (texto(licencia.fecha_recepcion) && texto(licencia.fecha_emision)
        && fechaTexto(licencia.fecha_recepcion) < fechaTexto(licencia.fecha_emision)) {
        errores.push('La fecha de recepción no puede ser anterior a la de emisión.');
    }

    if (!buscar(TIPOS_REPOSO, licencia.tipo_reposo)) {
        errores.push('Indique si el reposo es total o parcial.');
    } else if (licencia.tipo_reposo === 'PARCIAL' && !buscar(JORNADAS_REPOSO, licencia.jornada_reposo)) {
        errores.push('Indique si el reposo parcial es de mañana, tarde o noche.');
    }
    if (tipo && tipo.maternal && licencia.tipo_reposo === 'PARCIAL') {
        errores.push('Las licencias maternales son siempre de reposo total.');
    }

    if (tipo && tipo.requiereHijo) {
        if (!texto(licencia.hijo_rut)) errores.push('Ingrese el RUT del hijo.');
        if (!texto(licencia.hijo_fecha_nacimiento)) {
            errores.push('Ingrese la fecha de nacimiento del hijo.');
        } else if (texto(licencia.fecha_inicio)) {
            const nacimiento = fechaTexto(licencia.hijo_fecha_nacimiento);
            const inicio = fechaTexto(licencia.fecha_inicio);
            if (tipo.valor === 'MATERNAL_POSTNATAL' && inicio < nacimiento) {
                errores.push('El postnatal no puede empezar antes del nacimiento del hijo.');
            }
            if (tipo.valor === 'ENFERMEDAD_GRAVE_HIJO' && inicio >= unAnioDespues(nacimiento)) {
                errores.push('El tipo 4 es para hijos menores de un año: al inicio del reposo el hijo ya había cumplido un año.');
            }
        }
    }

    if (tipo && tipo.requiereAccidente) {
        if (!texto(licencia.accidente_fecha)) errores.push('Ingrese la fecha y hora del accidente.');
        // Booleano desde el backend; 'true' o 'false' desde el selector del formulario.
        if ([true, false, 'true', 'false'].indexOf(licencia.accidente_trayecto) === -1) {
            errores.push('Indique si el accidente fue de trayecto.');
        }
    }

    if (texto(licencia.operador) && !buscar(OPERADORES, licencia.operador)) {
        errores.push('El operador debe ser I-Med o Medipass.');
    }
    if (texto(licencia.profesional_tipo) && !buscar(TIPOS_PROFESIONAL, licencia.profesional_tipo)) {
        errores.push('El profesional debe ser médico cirujano, cirujano dentista o matrona.');
    }

    return errores;
}

/**
 * Errores de una no recepción; vacío si cumple. Recibe motivo, fecha_termino_relacion,
 * documento (true si viene un archivo o una URL) y glosa.
 */
function validarNoRecepcion(noRecepcion) {
    const motivo = buscar(MOTIVOS_NO_RECEPCION, noRecepcion.motivo);
    if (!motivo) return ['Seleccione el motivo de la no recepción.'];

    const errores = [];
    if (motivo.pideTermino) {
        if (!texto(noRecepcion.fecha_termino_relacion)) errores.push('Ingrese la fecha de término de la relación laboral.');
        if (!noRecepcion.documento) errores.push('Adjunte el documento que acredita el término de la relación laboral.');
    }
    if (motivo.pideGlosa && !texto(noRecepcion.glosa)) {
        errores.push('Indique el motivo por el cual no se tramitará la licencia.');
    }
    return errores;
}

module.exports = {
    TIPOS_LICENCIA,
    TIPOS_REPOSO,
    JORNADAS_REPOSO,
    OPERADORES,
    TIPOS_PROFESIONAL,
    OCUPACIONES,
    ACTIVIDADES_LABORALES,
    MOTIVOS_NO_RECEPCION,
    buscar,
    reglasDelTipo,
    fechaTexto,
    validarLicencia,
    validarNoRecepcion
};
